/**
 * Diff allowlist (Story 34.1 — Epic 34). Default-DENY.
 *
 * `diff_before` / `diff_after` may ONLY contain keys present in this per-resource
 * allowlist. Anything not listed is dropped — this is the structural guarantee
 * that secrets (password hashes, OAuth/access/refresh tokens, API keys, raw
 * secrets) can never enter the audit log, even if a careless caller passes a
 * whole row to `buildDiff`. The forbidden-key invariant is enforced by a unit
 * test (auditable-fields.test.ts) and by `buildDiff` at runtime.
 */
import type { ResourceType } from "./actions";

/**
 * Per-resource-type allowlist of fields that may be diff-captured. Only non-
 * secret, human-meaningful fields. A resource type absent from this map captures
 * NO diff (every key is denied).
 */
export const AUDITABLE_FIELDS: Partial<Record<ResourceType, readonly string[]>> = {
  report: ["name", "status", "kind", "clientId"],
  report_template: ["name", "description"],
  client: ["name", "industry", "status", "timezone"],
  custom_metric: ["name", "formula", "format"],
  client_goal: ["name", "metricKey", "targetValue", "period"],
  client_budget: ["name", "amount", "period", "integrationType"],
  client_markup: ["name", "type", "value"],
  tag: ["name", "color"],
  integration: ["platform", "status"],
  portal_link: ["label", "expiresAt"],
  dashboard_share_link: ["label", "expiresAt"],
  brand_share_link: ["label", "expiresAt"],
  dashboard: ["name", "description"],
  brand: ["name", "slug"],
  schedule: ["name", "cadence", "isActive"],
  task: ["title", "status", "dueAt"],
  alert: ["name", "platform", "metricKey", "condition", "threshold", "interval", "isActive"],
  team_member: ["role"],
  workspace: ["name"],
  tenant_settings: ["name", "plan"],
  tenant_branding: ["primaryColor", "logoUrl", "emailFromName"],
  user_settings: ["name", "email", "avatarUrl"],
} as const;

/**
 * Secret-bearing key names that MUST NEVER appear in any allowlist. The unit
 * test asserts no allowlist contains any of these; exported so other layers can
 * reuse the same denylist for defense-in-depth.
 */
export const FORBIDDEN_DIFF_KEYS: readonly string[] = [
  "password",
  "passwordhash",
  "password_hash",
  "token",
  "tokenhash",
  "token_hash",
  "accesstoken",
  "access_token",
  "refreshtoken",
  "refresh_token",
  "apikey",
  "api_key",
  "clientsecret",
  "client_secret",
  "secret",
  "encryptedtoken",
  "encrypted_token",
] as const;

const FORBIDDEN_SET: ReadonlySet<string> = new Set(FORBIDDEN_DIFF_KEYS);

/**
 * Secret-bearing SUBSTRING stems. A key is forbidden if its normalized form
 * CONTAINS any stem — so compound/affixed real column names (e.g. the OAuth
 * suffix style `access_token_encrypted` / `refresh_token_encrypted`, or
 * `oauth_client_secret`, `webhook_secret`) are caught even if a future story
 * adds them to an allowlist by mistake. Stems are deliberately specific (no bare
 * `key`, which would wrongly strip legitimate fields like `metricKey`).
 * False-positive stripping of a legit secret-named field is the SAFE direction
 * for an audit firewall; false-negative leakage is not.
 */
const FORBIDDEN_STEMS: readonly string[] = [
  "password",
  "secret",
  "token",
  "apikey",
  "credential",
  "privatekey",
];

/** True if a key name is a known/likely secret-bearer (case/separator-insensitive). */
export function isForbiddenDiffKey(key: string): boolean {
  const underscored = key.toLowerCase().replace(/[\s-]/g, "_");
  const stripped = underscored.replace(/_/g, "");
  if (FORBIDDEN_SET.has(underscored) || FORBIDDEN_SET.has(stripped)) return true;
  return FORBIDDEN_STEMS.some((stem) => stripped.includes(stem));
}

export interface Diff {
  before: Record<string, unknown>;
  after: Record<string, unknown>;
}

/**
 * Build an allowlisted, changed-keys-only diff for a resource type. Returns only
 * keys that (a) are in the resource's allowlist, (b) are NOT forbidden, and
 * (c) actually changed (before !== after by JSON value). Returns null when
 * nothing allowlisted changed.
 */
export function buildDiff(
  resourceType: ResourceType,
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
): Diff | null {
  const allow = AUDITABLE_FIELDS[resourceType];
  if (!allow || allow.length === 0) return null;

  const b = before ?? {};
  const a = after ?? {};
  const diffBefore: Record<string, unknown> = {};
  const diffAfter: Record<string, unknown> = {};
  let changed = false;

  for (const key of allow) {
    // Defense-in-depth: never emit a forbidden key even if mistakenly allowlisted.
    if (isForbiddenDiffKey(key)) continue;
    const bv = b[key];
    const av = a[key];
    if (JSON.stringify(bv) !== JSON.stringify(av)) {
      diffBefore[key] = bv ?? null;
      diffAfter[key] = av ?? null;
      changed = true;
    }
  }

  return changed ? { before: diffBefore, after: diffAfter } : null;
}
